/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { BasicModalProps } from 'src/core/components/BasicModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { AzureSkuInfo } from 'src/core/services/azure/azureSwaggers';
import { logLanguageResourceCreated, logResourceCreation } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountKind, AzureCsAccountStore, AzureSku } from 'src/core/stores/AzureCsAccountStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { Region } from 'src/core/types/regionTypes';
import { AzureCsAccountCreationModal } from 'src/modules/azure/components/AzureCsAccountCreationModal';
import { AzureCsAccountSelectModalV2 } from 'src/modules/azure/components/AzureCsAccountSelectModalV2';
import { azureResourceModalsMessages } from 'src/modules/azure/components/azureResourceModalsMessages';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';

export type AzureResourcesManagerProps = Pick<BasicModalProps<{}>, 'disabled'> & {
    selectedResourceId?: string;
    selectedSubscriptionId?: string;
    isSelectResourceVisible?: boolean;
    isCreateResourceVisible?: boolean;
    serviceTarget: AzureCsAccountKind;
    onSwitchResourceComplete?: () => void;
    onCreateResourceComplete?: () => void;
    onChangeSubscription?: (sbscriptionId: string) => void;
    onChangeSelectResourceVisible?: (isVisible: boolean) => void;
    onChangeCreateResourceVisible?: (isVisible: boolean) => void;
    onSelectResource?: (selectedResource: AzureCsAccountStore, defaultAction?: () => void) => void;
    strategies?: {
        sku?: (sku: AzureSkuInfo) => boolean;
        region?: (region: AzureSkuInfo) => boolean;
        resource?: (resource: AzureCsAccountStore) => boolean;
        subscription?: (subscription: AzureSubscriptionStore) => boolean;
    };
    onCreateResource?: (
        newResourceInfo: {
            sku: AzureSku;
            region: Region;
            resourceName: string;
            subscriptionId: string;
            resourceGroupName: string;
            isManagedIdentityEnabled: boolean;
        },
        defaultAction?: () => void
    ) => void;
};

export const AzureResourcesManager = observer((props: AzureResourcesManagerProps) => {
    const {
        strategies,
        serviceTarget,
        onSelectResource,
        onCreateResource,
        selectedResourceId,
        onChangeSubscription,
        selectedSubscriptionId,
        isSelectResourceVisible,
        isCreateResourceVisible,
        onSwitchResourceComplete,
        onCreateResourceComplete,
        onChangeSelectResourceVisible,
        onChangeCreateResourceVisible
    } = props;

    const [selectedResourceType, setSelectedResourceType] = React.useState<AzureCsAccountKind>(serviceTarget);
    const defaultStrategies: AzureResourcesManagerProps['strategies'] = {
        resource: () => true,
        subscription: () => true,
        sku: sku => sku.kind === selectedResourceType,
        region: region => region.kind === selectedResourceType
    };

    const filteringStrategies: AzureResourcesManagerProps['strategies'] = {
        sku: strategies?.sku ?? defaultStrategies.sku,
        region: strategies?.region ?? defaultStrategies.region,
        resource: strategies?.resource ?? defaultStrategies.resource,
        subscription: strategies?.subscription ?? defaultStrategies.subscription
    };

    const [isCreateCsAccountModalHidden, setIsCreateCsAccountModalHidden] = React.useState(true);

    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const { localize } = useLocalization();
    const defaultSelectedIds = {
        selectedResourceId: !!selectedResourceId?.length ? selectedResourceId : '',
        selectedResourceGroupId: '',
        selectedRegion: null as Region,
        selectedSkuCode: null as AzureSku['code'],
        selectedTenantId: rootStore.azureStore.session.tenantId,
        selectedSubscriptionId: !!selectedSubscriptionId?.length ? selectedSubscriptionId : rootStore.azureStore.session.subscriptionId
    };
    const [azureResourceDto, updateAzureResourceDto] = useUpdateState(defaultSelectedIds);

    const isLuisPortal = uiStore.portal.kind === 'luis';
    const resourceTypeOptions = React.useMemo(
        () =>
            [
                {
                    key: 'TextAnalytics',
                    text: localize(azureResourceModalsMessages.textAnalytics),
                    disabled: !isLuisPortal && uiStore.portal.subPath === 'translation'
                }
            ].concat(
                isLuisPortal
                    ? [{ key: 'LUIS.Authoring', text: localize(azureResourceModalsMessages.luisAuthoring), disabled: false }]
                    : [
                          {
                              key: 'TextTranslation',

                              text: localize(azureResourceModalsMessages.textTranslation),
                              disabled: uiStore.portal.subPath !== 'root' && uiStore.portal.subPath !== 'translation'
                          },
                          {
                              key: 'CognitiveServices',

                              text: localize(azureResourceModalsMessages.cognitiveServices),
                              disabled: uiStore.portal.subPath !== 'root'
                          }
                      ]
            ),
        [isLuisPortal]
    );

    React.useEffect(() => {
        if (selectedSubscriptionId) {
            updateAzureResourceDto({ selectedSubscriptionId });
        }
    }, [selectedSubscriptionId]);

    const azureResourceCreationTracker = useProgressTracker<AzureCsAccountStore>({
        onComplete: (newResource: AzureCsAccountStore) => {
            logLanguageResourceCreated(selectedResourceType);
            hideResourceCreationModal();
            onCreateResourceComplete?.();
            updateAzureResourceDto({ selectedResourceId: newResource.id });
        }
    });
    const changeCsAccountTracker = useProgressTracker({
        onComplete: () => {
            onChangeSelectResourceVisible?.(false);
            onChangeCreateResourceVisible?.(false);
            onSwitchResourceComplete?.();
        }
    });
    const {
        skus,
        regions,
        tenants,
        resources,
        skusTrackers,
        subscriptions,
        resourceGroups,
        tenantsTracker,
        regionsTrackers,
        reloadResources,
        resourcesTracker,
        subscriptionTracker,
        resourcesStoreHouse,
        resourceGroupsTracker
    } = useAzureResourcesManagement({
        skuStrategy: filteringStrategies.sku,
        regionStrategy: filteringStrategies.region,
        resourceStrategy: filteringStrategies.resource,
        subscriptionStrategy: filteringStrategies.subscription,
        subscriptionId: azureResourceDto.selectedSubscriptionId,
        configs: {
            resources: true,
            skuAndRegions: !isCreateCsAccountModalHidden,
            resourceGroups: !isCreateCsAccountModalHidden
        }
    });

    const filteredResources = React.useMemo(() => resources.filter(item => selectedResourceType === item.kind), [
        selectedResourceType,
        resources
    ]);

    React.useEffect(() => {
        if (onChangeCreateResourceVisible && !isCreateResourceVisible !== isCreateCsAccountModalHidden) {
            setIsCreateCsAccountModalHidden(!isCreateResourceVisible);
        }
    }, [isCreateResourceVisible]);

    React.useEffect(() => {
        if (isSelectResourceVisible) {
            updateAzureResourceDto(defaultSelectedIds);
        }
    }, [isSelectResourceVisible]);

    const hideResourceCreationModal = () => {
        setIsCreateCsAccountModalHidden(true);
        onChangeCreateResourceVisible?.(false);
        azureResourceCreationTracker.reset();
    };

    const switchToSelectedResource = (resource: AzureCsAccountStore) =>
        dispatcher.dispatch('switchAzureCsAccountInSession', {
            toastData: {},
            newAccount: false,
            csAccountStore: resource,
            tracker: changeCsAccountTracker
        });

    const createAzureCsAccount = ({
        resourceName,
        isManagedIdentityEnabled
    }: {
        resourceName: string;
        isManagedIdentityEnabled: boolean;
    }) => () =>
        dispatcher.dispatch('createAzureCsAccount', {
            keyName: resourceName,
            localStore: resourcesStoreHouse,
            tracker: azureResourceCreationTracker,
            id: azureResourceDto.selectedSubscriptionId,
            resourceGroupName: resourceGroups.find(rG => rG.id === azureResourceDto.selectedResourceGroupId).name,
            keyProps: {
                properties: {},
                kind: selectedResourceType,
                location: azureResourceDto.selectedRegion,
                sku: { code: azureResourceDto.selectedSkuCode },
                identity: { type: isManagedIdentityEnabled ? 'SystemAssigned' : 'None' }
            }
        });

    const submitSelectedResource = () => {
        const selectedResource = resources.find(r => r.id === azureResourceDto.selectedResourceId);

        if (onSelectResource) {
            onSelectResource(selectedResource, () => switchToSelectedResource(selectedResource));
        } else {
            switchToSelectedResource(selectedResource);
        }
    };

    const submitResourceCreation = ({
        resourceName,
        isManagedIdentityEnabled
    }: {
        resourceName: string;
        isManagedIdentityEnabled: boolean;
    }) => {
        if (onCreateResource) {
            onCreateResource(
                {
                    resourceName,
                    isManagedIdentityEnabled,
                    region: azureResourceDto.selectedRegion,
                    sku: { code: azureResourceDto.selectedSkuCode },
                    subscriptionId: azureResourceDto.selectedSubscriptionId,
                    resourceGroupName: resourceGroups.find(rG => rG.id === azureResourceDto.selectedResourceGroupId).name
                },
                createAzureCsAccount({ resourceName, isManagedIdentityEnabled })
            );
        } else {
            createAzureCsAccount({ resourceName, isManagedIdentityEnabled })();
        }
    };

    const handleDismissSelectModal = () => {
        if (selectedResourceId) {
            dispatcher.dispatch('updateCsAccountSelectModalProps', {
                isVisible: false,
                resourceId: null,
                subscriptionId: null
            });
        } else {
            onChangeSelectResourceVisible?.(false);
        }
    };

    return (
        <Stack>
            <AzureCsAccountSelectModalV2
                {...props}
                tenants={tenants}
                resources={filteredResources}
                subscriptions={subscriptions}
                hidden={!isSelectResourceVisible}
                onSubmit={submitSelectedResource}
                onReloadResources={reloadResources}
                serviceTarget={selectedResourceType}
                resourceTypeOptions={resourceTypeOptions}
                areTenantsLoading={tenantsTracker.isRunning}
                onDismiss={() => handleDismissSelectModal()}
                isResourceTypeDescriptionHidden={isLuisPortal}
                areResourcesLoading={resourcesTracker.isRunning}
                selectedTenantId={azureResourceDto.selectedTenantId}
                showResourcesTypeDropDown={serviceTarget !== 'LUIS'}
                areSubscriptionsLoading={subscriptionTracker.isRunning}
                selectedResourceId={azureResourceDto.selectedResourceId}
                disabled={changeCsAccountTracker.isRunning || props.disabled}
                selectedSubscriptionId={azureResourceDto.selectedSubscriptionId}
                onChangeResourceType={resourceType => setSelectedResourceType(resourceType)}
                errorMessage={changeCsAccountTracker.isFailed && changeCsAccountTracker.error?.message}
                onChangeResource={resourceId => updateAzureResourceDto({ selectedResourceId: resourceId })}
                onCreateResourceClicked={() => {
                    logResourceCreation(selectedResourceType);
                    setIsCreateCsAccountModalHidden(false);
                }}
                onChangeSubscription={subscriptionId =>
                    updateAzureResourceDto({ selectedSubscriptionId: subscriptionId, selectedResourceId: '' })
                }
            />

            <AzureCsAccountCreationModal
                skus={skus}
                regions={regions}
                subscriptions={subscriptions}
                resourcesGroups={resourceGroups}
                onSubmit={submitResourceCreation}
                serviceTarget={selectedResourceType}
                hidden={isCreateCsAccountModalHidden}
                onDismiss={hideResourceCreationModal}
                areSkusLoading={skusTrackers.isRunning}
                areRegionsLoading={regionsTrackers.isRunning}
                selectedRegion={azureResourceDto.selectedRegion}
                selectedSkuCode={azureResourceDto.selectedSkuCode}
                areSubscriptionsLoading={subscriptionTracker.isRunning}
                areResourceGroupsLoading={resourceGroupsTracker.isRunning}
                selectedSubscriptionId={azureResourceDto.selectedSubscriptionId}
                selectedResourceGroupId={azureResourceDto.selectedResourceGroupId}
                disabled={azureResourceCreationTracker.isRunning || props.disabled}
                onChangeSku={skuCode => updateAzureResourceDto({ selectedSkuCode: skuCode })}
                onChangeRegion={region => updateAzureResourceDto({ selectedRegion: region })}
                errorMessage={azureResourceCreationTracker.isFailed && azureResourceCreationTracker.error?.message}
                onChangeResourceGroup={resourceGroupId => updateAzureResourceDto({ selectedResourceGroupId: resourceGroupId })}
                onChangeSubscription={subscriptionId => {
                    updateAzureResourceDto({ selectedSubscriptionId: subscriptionId, selectedResourceId: '' });
                    onChangeSubscription?.(subscriptionId);
                }}
            />
        </Stack>
    );
});
